/**
 * @jsxRuntime classic
 * @jsx jsx
 */
import { jsx } from '@compiled/react';
import { FormattedMessage, type MessageDescriptor } from 'react-intl';

import { cssMap, cx } from '@atlaskit/css';
import Image from '@atlaskit/image';
import { Inline, Stack } from '@atlaskit/primitives/compiled';
import { token } from '@atlaskit/tokens';
import { CardContainer } from '@atlassian/company-hub-common';
import { GeneralImage, noDynamicCards } from '@atlassian/company-hub-common/assets';

import { CardSize, CardsLayout } from '../../../../types';
import type { CommonCardProps } from '../types';
import { messages } from './messages';

const styles = cssMap({
	container: {
		height: '100%',
	},
	imageBase: {
		backgroundColor: token('color.background.accent.gray.subtlest'),
	},
	imageFlexibleHeight: {
		minHeight: '0px',
		flex: '1 1 0',
	},
	imageFixedHeightMedium: {
		height: '140px',
	},
	imageFixedHeightLarge: {
		height: '200px',
	},
	innerImageSmall: {
		width: '60px',
		height: '60px',
		paddingTop: token('space.050'),
		paddingRight: token('space.100'),
		paddingBottom: token('space.100'),
		paddingLeft: token('space.050'),
	},
	titleBase: {
		paddingRight: token('space.150'),
		paddingLeft: token('space.150'),
		paddingTop: token('space.150'),
		paddingBottom: token('space.150'),
		font: token('font.body'),
		color: token('color.text'),
	},
	largeFont: {
		font: token('font.body.large'),
	},
	titleBlock: {
		height: '48px',
	},
	imageInline: {
		minWidth: '80px',
		height: '100%',
	},
	titleInline: {
		height: '100%',
	},
});

const SmallCard = ({ imageSrc, title }: { imageSrc?: string; title: MessageDescriptor }) => {
	return (
		<Inline testId="no-result-card-small" xcss={styles.container}>
			<Inline
				alignBlock="center"
				alignInline="center"
				xcss={cx(styles.imageBase, styles.imageInline)}
			>
				{imageSrc && (
					<Inline alignBlock="center" alignInline="center" xcss={styles.innerImageSmall}>
						<Image src={imageSrc} alt="no-result" testId="no-result-image" />
					</Inline>
				)}
			</Inline>
			<Inline alignBlock="center" xcss={cx(styles.titleBase, styles.titleInline)}>
				<FormattedMessage {...title} />
			</Inline>
		</Inline>
	);
};

const MediumLargeCard = ({
	isImageFixedHeight,
	cardSize = CardSize.MEDIUM,
	imageSrc,
	title,
}: {
	cardSize?: CardSize;
	imageSrc?: string;
	isImageFixedHeight?: boolean;
	title: MessageDescriptor;
}) => {
	const isLargeCard = cardSize === CardSize.LARGE;
	return (
		<Stack testId="no-result-card" space="space.025" xcss={styles.container}>
			<Inline
				alignBlock="center"
				alignInline="center"
				xcss={cx(
					styles.imageBase,
					isImageFixedHeight
						? isLargeCard
							? styles.imageFixedHeightLarge
							: styles.imageFixedHeightMedium
						: styles.imageFlexibleHeight,
				)}
			>
				{imageSrc && <Image src={imageSrc} alt="no-result" testId="no-result-image" />}
			</Inline>
			<Inline
				alignBlock="center"
				alignInline="center"
				xcss={cx(styles.titleBase, styles.titleBlock, isLargeCard && styles.largeFont)}
			>
				<FormattedMessage {...title} />
			</Inline>
		</Stack>
	);
};

export enum EmptyState {
	NO_FILTER = 'noFilter',
	NO_RESULTS = 'noResults',
}

type DynamicCardEmptyStateProps = {
	cardSize: CardSize;
	layout?: CardsLayout;
	state?: EmptyState;
} & Pick<CommonCardProps, 'cardDimensions'>;

const getStateContent = (state?: EmptyState) => {
	if (state === EmptyState.NO_FILTER) {
		return {
			imageSrc: noDynamicCards,
			title: messages.noFilterTitle,
		};
	}

	return {
		imageSrc: GeneralImage,
		title: messages.noResultsTitle,
	};
};

export const DynamicCardEmptyState = ({
	cardSize,
	layout,
	cardDimensions,
	state,
}: DynamicCardEmptyStateProps): JSX.Element => {
	const { height, minHeight, maxHeight } = cardDimensions;

	const { imageSrc, title } = getStateContent(state);

	if (layout === CardsLayout.FIXED_IMAGE_HEIGHT && cardSize !== CardSize.SMALL) {
		return (
			<CardContainer>
				<div
					style={{
						minHeight,
						maxHeight,
					}}
				>
					<MediumLargeCard
						isImageFixedHeight
						cardSize={cardSize}
						imageSrc={imageSrc}
						title={title}
					/>
				</div>
			</CardContainer>
		);
	}

	return (
		<CardContainer cardHeight={height}>
			{cardSize === CardSize.SMALL ? (
				<SmallCard imageSrc={imageSrc} title={title} />
			) : (
				<MediumLargeCard imageSrc={imageSrc} title={title} />
			)}
		</CardContainer>
	);
};
